import React from 'react';

export const FormulaLibraryGrid = ({
  formulas = [],
  loading = false,
  onCreateNew,
  onEdit,
  onTest,
  onViewVersions,
  onViewImpact,
  onViewAudit,
  onToggleStatus
}) => {
  if (loading) {
    return (
      <div className="formula-table-card" style={{ padding: '48px', textAlign: 'center', color: '#64748b' }}>
        <div style={{ display: 'inline-block', width: '32px', height: '32px', border: '3px solid #cbd5e1', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        <p style={{ marginTop: '14px', fontWeight: 600, fontSize: '14px', color: '#334155' }}>
          Loading formula registry...
        </p>
      </div>
    );
  }

  if (formulas.length === 0) {
    return (
      <div className="formula-table-card" style={{ padding: '56px 24px', textAlign: 'center' }}>
        <div style={{ fontSize: '44px', marginBottom: '14px' }}>🧪</div>
        <h3 style={{ margin: '0 0 8px 0', color: '#0f172a', fontSize: '18px', fontWeight: 700 }}>
          No Clinical Formulas Configured
        </h3>
        <p style={{ margin: '0 auto 20px auto', color: '#64748b', fontSize: '14px', maxWidth: '480px', lineHeight: 1.5 }}>
          There are no calculated parameter formulas configured yet. Select a Pathology Department and Investigation to configure your first formula.
        </p>
        <button
          className="btn-primary-gradient"
          style={{ margin: '0 auto' }}
          onClick={onCreateNew}
        >
          + Configure New Formula
        </button>
      </div>
    );
  }

  return (
    <div className="formula-table-card">
      <table className="formula-table">
        <thead>
          <tr>
            <th>Formula Name & Code</th>
            <th>Department</th>
            <th>Investigation / Test</th>
            <th>Target Calculated Parameter</th>
            <th>Type</th>
            <th>Version</th>
            <th>Inputs</th>
            <th>Status</th>
            <th style={{ textAlign: 'right' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {formulas.map((f) => (
            <tr key={f.formulaId}>
              <td>
                <div style={{ fontWeight: 700, color: '#0f172a' }}>{f.formulaName}</div>
                <div style={{ marginTop: '4px' }}>
                  <span className="formula-code-badge">{f.formulaCode}</span>
                </div>
              </td>
              <td>
                <span style={{ fontSize: '13px', color: '#334155', fontWeight: 500 }}>
                  {f.departmentName || '-'}
                </span>
              </td>
              <td>
                <div style={{ fontSize: '13px', color: '#0f172a', fontWeight: 600 }}>
                  {f.investigationName || '-'}
                </div>
                {f.investigationCode && (
                  <span style={{ fontSize: '11px', color: '#64748b' }}>
                    Code: {f.investigationCode}
                  </span>
                )}
              </td>
              <td>
                <div style={{ fontSize: '13px', color: '#1d4ed8', fontWeight: 600 }}>
                  {f.targetObsName || `Obs #${f.targetObsId}`}
                </div>
                <div style={{ fontSize: '11px', color: '#64748b' }}>
                  {f.targetObsCode ? `Code: ${f.targetObsCode}` : ''} {f.targetObsUnit ? `(${f.targetObsUnit})` : ''}
                </div>
              </td>
              <td>
                <span style={{ fontSize: '12.5px', fontWeight: 500, color: '#475569' }}>
                  {f.formulaType}
                </span>
              </td>
              <td>
                <span style={{
                  fontSize: '11.5px',
                  fontWeight: 700,
                  backgroundColor: '#f1f5f9',
                  color: '#1e293b',
                  padding: '3px 8px',
                  borderRadius: '12px'
                }}>
                  v{f.currentVersion}
                </span>
              </td>
              <td>
                <span style={{ fontSize: '12.5px', color: '#64748b', fontWeight: 500 }}>
                  {f.inputCount || 0} input{f.inputCount !== 1 ? 's' : ''}
                </span>
              </td>
              <td>
                <span className={`status-badge status-${(f.status || 'draft').toLowerCase()}`}>
                  ● {f.status}
                </span>
              </td>
              <td style={{ textAlign: 'right' }}>
                <div style={{ display: 'inline-flex', gap: '6px', alignItems: 'center' }}>
                  <button
                    onClick={() => onTest(f)}
                    title="Live Test Simulation"
                    style={{
                      background: '#f0fdf4',
                      border: '1px solid #bbf7d0',
                      color: '#166534',
                      padding: '5px 10px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    ⚡ Test
                  </button>
                  <button
                    onClick={() => onEdit(f)}
                    title="Edit Formula"
                    style={{
                      background: '#eff6ff',
                      border: '1px solid #bfdbfe',
                      color: '#1e40af',
                      padding: '5px 10px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    ✏️ Edit
                  </button>
                  <button
                    onClick={() => onViewImpact(f)}
                    title="Impact & Dependency Graph"
                    style={{
                      background: '#fff7ed',
                      border: '1px solid #fed7aa',
                      color: '#9a3412',
                      padding: '5px 8px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    🔗
                  </button>
                  <button
                    onClick={() => onViewVersions(f)}
                    title="Version History"
                    style={{
                      background: '#f8fafc',
                      border: '1px solid #cbd5e1',
                      color: '#475569',
                      padding: '5px 8px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    📜
                  </button>
                  <button
                    onClick={() => onToggleStatus(f)}
                    title={f.status === 'Active' ? 'Deactivate Formula' : 'Activate Formula'}
                    style={{
                      background: f.status === 'Active' ? '#fef2f2' : '#f0fdf4',
                      border: f.status === 'Active' ? '1px solid #fecaca' : '1px solid #bbf7d0',
                      color: f.status === 'Active' ? '#991b1b' : '#166534',
                      padding: '5px 8px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    {f.status === 'Active' ? '⏸' : '▶'}
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default FormulaLibraryGrid;
